import React from 'react';

/**
 * "My Market Maps" — list of past analyses owned by or shared with the user.
 * Renders below the wizard (when no analysis is open) and is always visible
 * outside the AnalysisResults conditional.
 *
 * Extracted from AnalysisResults.jsx (Feb 2026 — bug fix: list was nested
 * inside `{currentStep === 4}` AND had a `currentStep !== 4` guard, so it
 * never rendered).
 */
export default function HistoryList({
  history,
  formatCurrency,
  loadAnalysis,
  toggleSaveAnalysis,
  toggleVisibility,
  deleteAnalysis,
  rerunFromHistory,
}) {
  if (!history || history.length === 0) return null;

  return (
    <div className="mt-8 bg-white rounded-bcm-lg shadow-bcm-card border border-silver/70 p-6" data-testid="history-list">
      <h3 className="bcm-subhead text-xl mb-4">My Market Maps</h3>
      <div className="space-y-3">
        {history.slice(0, 10).map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between p-4 border border-silver rounded-bcm-md hover:bg-silver/30 cursor-pointer group transition-colors"
            onClick={() => loadAnalysis(item.id)}
            data-testid={`history-item-${item.id}`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="bcm-body-emphasis text-sm group-hover:text-bcm-orange transition-colors">
                  {item.product_name}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-display font-semibold ${
                    item.visibility === 'public'
                      ? 'bg-billiard/15 text-billiard'
                      : 'bg-silver text-paradiso'
                  }`}
                >
                  {item.visibility === 'public' ? 'Public' : 'Private'}
                </span>
                {item.is_saved && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-sandy/25 text-[#8a5a1e] font-display font-semibold">
                    Saved
                  </span>
                )}
              </div>
              <div className="bcm-caption mt-1">
                {item.geography} • {item.industry}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right mr-3">
                <div className="font-display font-semibold text-bcm-orange text-sm">
                  {formatCurrency(item.market_size)}
                </div>
                <div className="bcm-caption">
                  {new Date(item.timestamp).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => toggleSaveAnalysis(e, item.id, item.is_saved)}
                  className={`p-2 rounded-bcm-md transition-colors ${
                    item.is_saved
                      ? 'text-sandy hover:bg-sandy/15'
                      : 'text-paradiso hover:bg-silver/60 hover:text-sandy'
                  }`}
                  title={item.is_saved ? 'Unsave' : 'Save'}
                  data-testid={`save-btn-${item.id}`}
                >
                  <svg className="w-5 h-5" fill={item.is_saved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
                {item.is_owner !== false && (
                  <button
                    onClick={(e) => toggleVisibility(e, item.id, item.visibility)}
                    className={`p-2 rounded-bcm-md transition-colors ${
                      item.visibility === 'public'
                        ? 'text-billiard hover:bg-billiard/10'
                        : 'text-paradiso hover:bg-silver/60 hover:text-billiard'
                    }`}
                    title={item.visibility === 'public' ? 'Make Private' : 'Make Public'}
                    data-testid={`visibility-btn-${item.id}`}
                  >
                    {item.visibility === 'public' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                  </button>
                )}
                {item.is_owner !== false && (
                  <button
                    onClick={(e) => deleteAnalysis(e, item.id, item.product_name)}
                    className="p-2 rounded-bcm-md text-paradiso hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Delete"
                    data-testid={`delete-btn-${item.id}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={(e) => rerunFromHistory(e, item.id)}
                  className="p-2 rounded-bcm-md text-paradiso hover:bg-bcm-orange/10 hover:text-bcm-orange transition-colors"
                  title="Edit & Re-run"
                  data-testid={`rerun-btn-${item.id}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-silver/70">
        <p className="bcm-caption text-center">
          Click any analysis to open • Hover an item for save, re-run, and delete actions
        </p>
      </div>
    </div>
  );
}
